'use client';

import { assetUrl } from '@/lib/directus/assetUrl';
import { devLog } from '@/lib/logging';
import { DirectusImage } from '@/types/directus';
import { PropsWithClassName } from '@/types/shared';
import clsx from 'clsx';
import Image from 'next/image';
import { useCallback, useState } from 'react';

import './BlogFeaturedArticleImage.scss';

const DEFAULT_WIDTH = 628;

const DEFAULT_HEIGHT = 600;

type Props = PropsWithClassName & {
  featuredArticleImage: undefined | DirectusImage;
  mobile?: boolean;
};

const PLACEHOLDER_SRC = '/blog-placeholders/featured-article-placeholder.jpg';

export const BlogFeaturedArticleImage: React.FC<Props> = ({
  featuredArticleImage,
  className,
  mobile = false,
}) => {
  const [imgSrc, setImgSrc] = useState<undefined | string>(() => {
    const url = assetUrl(featuredArticleImage);

    if (url) return url;

    return PLACEHOLDER_SRC;
  });

  const handleImageError = useCallback(() => {
    devLog(`BlogFeaturedArticleImage: image loading error`);

    if (imgSrc === PLACEHOLDER_SRC) {
      devLog(
        `BlogFeaturedArticleImage: image placeholder error; ${PLACEHOLDER_SRC}`
      );
      setImgSrc(undefined);

      return;
    }

    setImgSrc(undefined);
  }, [imgSrc]);

  if (!imgSrc) return null;

  return (
    <Image
      className={clsx(
        'BlogFeaturedArticleImage',
        { 'BlogFeaturedArticleImage--mobile': mobile },
        className
      )}
      src={imgSrc}
      alt='featured-article'
      onError={handleImageError}
      width={DEFAULT_WIDTH}
      height={DEFAULT_HEIGHT}
    />
  );
};
